import type { Locale, RecognizedCustomerView, Service, Slot } from '@360face/domain';
import type { CheckInMatch, KioskVisit } from '../../api/client';
import { customerErrors, emptyCustomer, type CustomerDraft, type CustomerField } from '../../kiosk/flow';
import { DEFAULT_COUNTRY, toE164, type CountryCode } from '../../kiosk/phone';

/**
 * Kiosk v2 navigation (owner 16.10.2026: second version to compare with v1).
 *
 * Booking (5 steps): home → service (variants inside the card) → time (days + times + therapist on
 *   one screen) → extras (≤3 suggestions + gift) → contact → confirm → [today: pay → questionnaire] → done
 * Check-in: home → find (phone · card · ClassPass) → hello ("is this you?" + "I'm here")
 *   → offer (one optional extra, only if it fits) → pay → questionnaire → done
 * ClassPass: find → cp (times) → cp_confirm → offer → questionnaire → done
 * Gift: home → gift (value + delivery on one screen) → gift_phone → [e-mail: gift_card] → pay | done
 */
export type V2Step =
  | 'welcome'
  | 'home'
  /** «Saber mais sobre nós» — for a visitor from the street (owner 03.11.2026). */
  | 'about'
  | 'service'
  | 'time'
  | 'extras'
  | 'contact'
  | 'confirm'
  | 'find'
  | 'hello'
  | 'cp'
  | 'cp_confirm'
  | 'offer'
  | 'gift'
  | 'gift_phone'
  | 'gift_card'
  | 'pay'
  | 'questionnaire'
  | 'done';

export type V2Mode = 'booking' | 'checkin' | 'classpass' | 'gift';

export interface V2State {
  locale: Locale;
  step: V2Step;
  mode: V2Mode;
  /** Booking */
  cardId?: string;
  service?: Service;
  date?: string;
  staffFilter?: string;
  slot?: Slot;
  addonIds: string[];
  /** Home-care products (ids `p-…`), shown quietly under the extras (owner 16.10.2026). */
  productIds: string[];
  bookingGiftId?: string;
  customer: CustomerDraft;
  /**
   * Details step (owner 17.10.2026): what the server said about the typed phone (E.164), and the
   * client's answer to "Is this you, Ana M.?". With "yes" the name and e-mail are not typed.
   */
  known?: { phone: string; view: RecognizedCustomerView; answer?: 'yes' | 'no' };
  /** One per draft: a retried submit never creates a second booking or gift sale. */
  idempotencyKey: string;
  /** The client's comment to the therapist, on the confirmation screen (owner 26.10.2026). */
  comment?: string;
  /** The booking just made is for another day: no payment or questionnaire now. */
  future?: boolean;
  /** Check-in */
  country: CountryCode;
  phone: string;
  card?: string;
  misses: number;
  miss: boolean;
  matches: CheckInMatch[];
  classPass?: { ref: string; start: string; fullName: string };
  /** Gift certificate */
  giftValueId?: string;
  giftDelivery?: 'pickup' | 'email';
  giftEmail: string;
  giftCard: { to: string; from: string; message: string };
  visit?: KioskVisit;
  /** Error code shown on the current screen (e.g. SLOT_TAKEN). */
  notice?: string;
  /**
   * Prepared on the client's own phone before the visit (owner 16.10.2026, link in the Altegio
   * reminder): find → "yes, it's me" → extras · gift · payment · form. No arrival, no reset timer.
   */
  remote?: boolean;
}

export type V2Event =
  | { type: 'START'; locale: Locale }
  | { type: 'START_REMOTE'; locale: Locale }
  | { type: 'SET_LOCALE'; locale: Locale }
  | { type: 'GO_BOOK' }
  | { type: 'GO_CHECKIN' }
  | { type: 'GO_GIFT' }
  | { type: 'GO_ABOUT' }
  | { type: 'PICK_SERVICE'; cardId: string; service: Service }
  | { type: 'SERVICE_DONE' }
  | { type: 'PICK_DATE'; date: string }
  | { type: 'FILTER_STAFF'; staffId?: string }
  | { type: 'PICK_SLOT'; slot: Slot }
  | { type: 'TOGGLE_ADDON'; id: string }
  | { type: 'TOGGLE_PRODUCT'; id: string }
  | { type: 'PICK_GIFT'; giftId?: string }
  | { type: 'EXTRAS_DONE' }
  | { type: 'EDIT_CUSTOMER'; patch: Partial<CustomerDraft> }
  | { type: 'EDIT_COMMENT'; comment: string }
  | { type: 'RECOGNIZED'; phone: string; view: RecognizedCustomerView }
  | { type: 'KNOWN_ANSWER'; answer: 'yes' | 'no' }
  | { type: 'RECOGNITION_EXPIRED' }
  | { type: 'CONTACT_DONE' }
  | { type: 'BOOKED'; visit: KioskVisit; today: string }
  | { type: 'SLOT_TAKEN' }
  | { type: 'EDIT_PHONE'; phone?: string; country?: CountryCode }
  | { type: 'CARD_SCANNED'; code: string }
  | { type: 'MATCHES'; matches: CheckInMatch[] }
  | { type: 'DISMISS_MISS' }
  | { type: 'CHECKED_IN'; visit: KioskVisit }
  | { type: 'GO_CLASSPASS' }
  | { type: 'CP_PICK'; ref: string; start: string; fullName: string }
  | { type: 'CP_CHECKED_IN'; visit: KioskVisit }
  | { type: 'OFFER_DONE'; visit?: KioskVisit }
  | { type: 'GIFT_VALUE'; giftId: string }
  | { type: 'GIFT_DELIVERY'; delivery: 'pickup' | 'email' }
  | { type: 'GIFT_NEXT' }
  | { type: 'EDIT_GIFT_PHONE'; phone?: string; country?: CountryCode }
  | { type: 'EDIT_GIFT_EMAIL'; email: string }
  | { type: 'EDIT_GIFT_CARD'; field: 'to' | 'from' | 'message'; value: string }
  | { type: 'GIFT_CREATED'; visit: KioskVisit }
  | { type: 'PAID'; visit?: KioskVisit }
  | { type: 'QUESTIONNAIRE_DONE'; visit?: KioskVisit }
  | { type: 'GOTO'; step: 'service' | 'time' | 'extras' | 'contact' }
  | { type: 'BACK' }
  | { type: 'RESET' };

/** Booking steps for the progress line (1-based position, 5 in all). */
export const BOOKING_STEPS: V2Step[] = ['service', 'time', 'extras', 'contact', 'confirm'];

export function v2Initial(key: () => string = () => crypto.randomUUID(), locale: Locale = 'pt'): V2State {
  return {
    locale,
    step: 'welcome',
    mode: 'booking',
    addonIds: [],
    productIds: [],
    customer: { ...emptyCustomer },
    idempotencyKey: key(),
    country: DEFAULT_COUNTRY,
    phone: '',
    misses: 0,
    miss: false,
    matches: [],
    giftEmail: '',
    giftCard: { to: '', from: '', message: '' },
  };
}

const BACK: Partial<Record<V2Step, V2Step>> = {
  about: 'home',
  service: 'home',
  time: 'service',
  extras: 'time',
  contact: 'extras',
  confirm: 'contact',
  find: 'home',
  hello: 'find',
  cp: 'find',
  cp_confirm: 'cp',
  gift: 'home',
  gift_phone: 'gift',
  gift_card: 'gift_phone',
};

/** Max extras per booking (Booksy/Fresha limit the number; v1 used 5). */
export const MAX_ADDONS = 3;
/** Max home-care products per booking (same as v1). */
export const MAX_PRODUCTS = 5;

export function v2Reducer(state: V2State, ev: V2Event, key: () => string = () => crypto.randomUUID()): V2State {
  const s: V2State = { ...state, notice: undefined };
  switch (ev.type) {
    case 'RESET':
      // Privacy: nothing of the previous customer survives a reset.
      if (state.remote) return remoteStart(key, state.locale);
      return v2Initial(key);
    case 'START_REMOTE':
      return state.step === 'welcome' ? remoteStart(key, ev.locale) : state;
    case 'START':
      return state.step === 'welcome' ? { ...s, locale: ev.locale, step: 'home' } : state;
    case 'SET_LOCALE':
      return { ...state, locale: ev.locale };
    case 'GO_BOOK':
      // also from «Saber mais» — its «Marcar agora» goes straight into the booking
      return state.step === 'home' || state.step === 'about'
        ? { ...fresh(state, key), mode: 'booking', step: 'service' }
        : state;
    case 'GO_CHECKIN':
      return state.step === 'home' ? { ...fresh(state, key), mode: 'checkin', step: 'find' } : state;
    case 'GO_GIFT':
      return state.step === 'home' ? { ...fresh(state, key), mode: 'gift', step: 'gift' } : state;
    case 'GO_ABOUT':
      return state.step === 'home' ? { ...s, step: 'about' } : state;

    case 'PICK_SERVICE':
      if (state.step !== 'service') return state;
      // another treatment: time and extras belonged to the previous choice
      return state.service?.id === ev.service.id
        ? { ...s, cardId: ev.cardId }
        : {
            ...s,
            cardId: ev.cardId,
            service: ev.service,
            date: undefined,
            slot: undefined,
            addonIds: [],
            bookingGiftId: undefined,
          };
    case 'SERVICE_DONE':
      return state.step === 'service' && state.service ? { ...s, step: 'time' } : state;
    case 'PICK_DATE':
      return state.step === 'time' ? { ...s, date: ev.date } : state;
    case 'FILTER_STAFF':
      return state.step === 'time' ? { ...s, staffFilter: ev.staffId } : state;
    case 'PICK_SLOT': {
      if (state.step !== 'time') return state;
      const same = state.slot && state.slot.staffId === ev.slot.staffId && state.slot.start === ev.slot.start;
      // another therapist or time = other free minutes after the treatment: extras are chosen again
      return {
        ...s,
        step: 'extras',
        slot: ev.slot,
        date: ev.slot.start.slice(0, 10),
        addonIds: same ? state.addonIds : [],
        bookingGiftId: same ? state.bookingGiftId : undefined,
      };
    }
    case 'TOGGLE_PRODUCT': {
      if (state.step !== 'extras' && state.step !== 'offer') return state;
      const has = state.productIds.includes(ev.id);
      if (!has && state.productIds.length >= MAX_PRODUCTS) return state;
      return { ...s, productIds: has ? state.productIds.filter((x) => x !== ev.id) : [...state.productIds, ev.id] };
    }
    case 'TOGGLE_ADDON': {
      if (state.step !== 'extras' && state.step !== 'offer') return state;
      const has = state.addonIds.includes(ev.id);
      if (!has && state.addonIds.length >= MAX_ADDONS) return state;
      return { ...s, addonIds: has ? state.addonIds.filter((x) => x !== ev.id) : [...state.addonIds, ev.id] };
    }
    case 'PICK_GIFT':
      return state.step === 'extras' ? { ...s, bookingGiftId: ev.giftId } : state;
    case 'EXTRAS_DONE':
      return state.step === 'extras' ? { ...s, step: 'contact' } : state;
    case 'EDIT_COMMENT':
      return state.step === 'confirm' ? { ...state, comment: ev.comment.slice(0, 300) } : state;
    case 'EDIT_CUSTOMER': {
      if (state.step !== 'contact') return state;
      const customer = { ...state.customer, ...ev.patch };
      // another number: the earlier "is this you?" no longer applies
      const phone = toE164(customer.phoneCountry, customer.phone);
      return { ...state, customer, known: state.known && state.known.phone === phone ? state.known : undefined };
    }
    case 'RECOGNIZED': {
      if (state.step !== 'contact') return state;
      if (toE164(state.customer.phoneCountry, state.customer.phone) !== ev.phone) return state;
      return { ...state, known: { phone: ev.phone, view: ev.view } };
    }
    case 'KNOWN_ANSWER':
      return state.step === 'contact' && state.known?.view.known
        ? { ...state, known: { ...state.known, answer: ev.answer } }
        : state;
    case 'RECOGNITION_EXPIRED':
      // the token ran out (e.g. a long pause): back to the details, typed this time
      return state.step === 'confirm' && state.known
        ? { ...s, step: 'contact', known: { ...state.known, answer: 'no' }, notice: 'RECOGNITION_EXPIRED' }
        : state;
    case 'CONTACT_DONE':
      return state.step === 'contact' && contactErrors(state).length === 0 ? { ...s, step: 'confirm' } : state;
    case 'GOTO':
      // "Change" on the confirmation screen jumps back to a booking step; later choices stay.
      return state.step === 'confirm' && state.mode === 'booking' ? { ...s, step: ev.step } : state;
    case 'BOOKED': {
      if (state.step !== 'confirm') return state;
      // Owner 14.10.2026: payment method and questionnaire only for a visit today.
      const today = state.slot?.start.slice(0, 10) === ev.today;
      return today
        ? { ...s, step: 'pay', visit: ev.visit, future: false }
        : { ...s, step: 'done', visit: ev.visit, future: true };
    }
    case 'SLOT_TAKEN':
      return state.step === 'confirm' ? { ...s, step: 'time', slot: undefined, notice: 'SLOT_TAKEN' } : state;

    case 'EDIT_PHONE':
      return state.step === 'find'
        ? {
            ...state,
            phone: ev.phone ?? state.phone,
            country: ev.country ?? state.country,
            card: ev.phone !== undefined && ev.phone !== state.phone ? undefined : state.card,
          }
        : state;
    case 'CARD_SCANNED':
      return state.step === 'find' ? { ...s, card: ev.code, miss: false } : state;
    case 'MATCHES':
      if (state.step !== 'find') return state;
      if (ev.matches.length === 0) return { ...s, miss: true, misses: state.misses + 1 };
      return { ...s, step: 'hello', matches: ev.matches, misses: 0, miss: false };
    case 'DISMISS_MISS':
      return { ...state, miss: false };
    case 'CHECKED_IN':
      return state.step === 'hello' ? { ...s, step: 'offer', visit: ev.visit, addonIds: [], productIds: [] } : state;
    case 'GO_CLASSPASS':
      return state.step === 'find' ? { ...s, mode: 'classpass', step: 'cp', classPass: undefined, miss: false } : state;
    case 'CP_PICK':
      return state.step === 'cp'
        ? { ...s, step: 'cp_confirm', classPass: { ref: ev.ref, start: ev.start, fullName: ev.fullName } }
        : state;
    case 'CP_CHECKED_IN':
      return state.step === 'cp_confirm'
        ? { ...s, step: 'offer', visit: ev.visit, addonIds: [], productIds: [] }
        : state;
    case 'OFFER_DONE':
      if (state.step !== 'offer') return state;
      // ClassPass is paid through ClassPass: no payment question
      if (state.mode === 'classpass') return formStep(s, ev.visit ?? state.visit);
      return { ...s, step: 'pay', visit: ev.visit ?? state.visit };

    case 'GIFT_VALUE':
      return state.step === 'gift' ? { ...s, giftValueId: ev.giftId } : state;
    case 'GIFT_DELIVERY':
      return state.step === 'gift' ? { ...s, giftDelivery: ev.delivery } : state;
    case 'GIFT_NEXT':
      if (state.step === 'gift') return state.giftValueId && state.giftDelivery ? { ...s, step: 'gift_phone' } : state;
      if (state.step === 'gift_phone' && state.giftDelivery === 'email') return { ...s, step: 'gift_card' };
      return state;
    case 'EDIT_GIFT_PHONE':
      return state.step === 'gift_phone'
        ? {
            ...state,
            customer: {
              ...state.customer,
              phoneCountry: ev.country ?? state.customer.phoneCountry,
              phone: ev.phone ?? state.customer.phone,
            },
          }
        : state;
    case 'EDIT_GIFT_EMAIL':
      return state.step === 'gift_phone' ? { ...state, giftEmail: ev.email } : state;
    case 'EDIT_GIFT_CARD':
      return state.step === 'gift_card' ? { ...state, giftCard: { ...state.giftCard, [ev.field]: ev.value } } : state;
    case 'GIFT_CREATED':
      // e-mail: staff agree the payment with the buyer; pickup: the method is chosen here
      return state.step === 'gift_phone' || state.step === 'gift_card'
        ? { ...s, step: state.giftDelivery === 'email' ? 'done' : 'pay', visit: ev.visit }
        : state;

    case 'PAID':
      if (state.step !== 'pay') return state;
      if (state.mode === 'gift') return { ...s, step: 'done', visit: ev.visit ?? state.visit };
      return formStep(s, ev.visit ?? state.visit);
    case 'QUESTIONNAIRE_DONE':
      return state.step === 'questionnaire' ? { ...s, step: 'done', visit: ev.visit ?? state.visit } : state;

    case 'BACK': {
      if (state.remote && state.step === 'find') return state;
      // after check-in the visit exists: nothing to go back to
      if (state.step === 'cp') return { ...s, mode: 'checkin', step: 'find' };
      const to = BACK[state.step];
      return to ? { ...s, step: to } : state;
    }
  }
}

/**
 * After payment: the health form question, unless a valid form is already on file for this client
 * (owner 16.10.2026 — known by the phone number, never asked again while it is valid).
 */
function formStep(s: V2State, visit: KioskVisit | undefined): V2State {
  const onFile = Boolean(visit?.formOnFile ?? s.visit?.formOnFile);
  return {
    ...s,
    step: onFile ? 'done' : 'questionnaire',
    visit: visit && s.visit?.formOnFile ? { ...visit, formOnFile: true } : visit,
  };
}

/** The phone page starts at "find your booking", in the phone's language. */
function remoteStart(key: () => string, locale: Locale): V2State {
  return { ...v2Initial(key, locale), mode: 'checkin', step: 'find', remote: true };
}

/** A new journey from the home screen: keep the language, forget everything else. */
function fresh(state: V2State, key: () => string): V2State {
  return { ...v2Initial(key, state.locale), step: state.step };
}

/** The returning client said "yes, it's me": name and e-mail come from the server. */
export function knownYes(state: V2State): boolean {
  return Boolean(state.known?.view.known && state.known.answer === 'yes' && state.known.view.token);
}

/**
 * Missing details on the contact step. A recognised client ("yes, it's me") types nothing but the
 * phone; the e-mail only when none is on file, the privacy tick only when no earlier Hub booking has it.
 */
export function contactErrors(state: V2State): CustomerField[] {
  const c = state.customer;
  if (!knownYes(state)) return customerErrors(c);
  const view = state.known!.view;
  const errs: CustomerField[] = [];
  if (!toE164(c.phoneCountry, c.phone)) errs.push('phone');
  if (!view.emailMask && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email.trim())) errs.push('email');
  if (!view.privacyOnFile && !c.privacy) errs.push('privacy');
  return errs;
}

/** 1-based booking step for the progress line; undefined outside the booking steps. */
export function bookingStep(state: V2State): number | undefined {
  if (state.mode !== 'booking') return undefined;
  const i = BOOKING_STEPS.indexOf(state.step);
  return i < 0 ? undefined : i + 1;
}

/** Steps where "Back" makes sense (not after a visit exists, not on the final screens). */
export function canGoBack(state: V2State): boolean {
  if (state.remote && state.step === 'find') return false;
  return BACK[state.step] !== undefined;
}

/** Seconds of inactivity before the "Are you still there?" warning, and how long it waits. */
export const IDLE_WARN_SECONDS = 70;
export const IDLE_GRACE_SECONDS = 20;
/** The final screen closes by itself (with a visible countdown). */
export const DONE_SECONDS = 15;
